import { useState, useCallback } from 'react';
import useHookActions from './useHookActions';
import { draftHooks, createHook, deleteHook, reorderHooks } from '../../api/hooks';
import { orderWithDraftsLast } from './courseOverview';

export const GENERATE_MORE = 3;

// Everything the approval pane can do to one course's hooks, shared by
// the Hooks page and the course editor. `hooks` may be the course's own
// list or the page's all-courses list — actions address rows by id.
// `reload` refetches after multi-row changes; `setError` gets failures.
export default function useCourseApproval({ token, courseId, hooks, setHooks, reload, setError }) {
    const [busy, setBusy] = useState('');
    const { run, toggleApproval, remove } = useHookActions({ token, setHooks, setError, onDeleted: reload });

    const withBusy = useCallback((label, action) => run(async () => {
        setBusy(label);
        try {
            await action();
        } finally {
            setBusy('');
        }
    }), [run]);

    const courseHooks = hooks.filter(h => h.course_id === courseId);

    // Ask the drafter for a few hooks on different facts and save them as
    // drafts in one go; the editor then approves the strongest and deletes
    // the rest. The server returns fewer when the content is thin.
    const generateMore = useCallback(() => withBusy('generate', async () => {
        const drafts = await draftHooks(token, courseId, GENERATE_MORE);
        for (const draft of drafts) {
            // Sequential on purpose: positions are assigned server-side.
            // eslint-disable-next-line no-await-in-loop
            await createHook(token, courseId, draft);
        }
        if (drafts.length === 0) setError('The drafter found nothing new to ask about this course.');
        await reload();
    }), [withBusy, token, courseId, reload, setError]);

    // Once the keepers are approved, clear the leftover drafts in one go.
    const deleteRemainingDrafts = useCallback(() => {
        const drafts = courseHooks.filter(h => h.status !== 'approved');
        if (drafts.length === 0) return;
        if (!window.confirm(`Delete the remaining ${drafts.length} draft${drafts.length === 1 ? '' : 's'} for this course?`)) return;
        withBusy('delete', async () => {
            for (const draft of drafts) {
                // eslint-disable-next-line no-await-in-loop
                await deleteHook(token, draft.id);
            }
            await reload();
        });
    }, [courseHooks, withBusy, token, reload]);

    // Reorder the approved hooks (the order the app asks them in); drafts
    // keep their place behind them. On failure the server order comes back.
    const reorderApproved = useCallback((approvedInNewOrder) => withBusy('reorder', async () => {
        const ordered = await reorderHooks(token, courseId, orderWithDraftsLast(approvedInNewOrder, courseHooks));
        setHooks(prev => {
            const byId = Object.fromEntries(ordered.map(h => [h.id, h]));
            return prev.map(h => byId[h.id] || h);
        });
    }), [withBusy, token, courseId, courseHooks, setHooks]);

    return { busy, toggleApproval, remove, generateMore, deleteRemainingDrafts, reorderApproved };
}
